import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  User,
  Search,
  Download,
  Eye,
  Edit,
  Send,
  RefreshCw,
  Plus,
  X,
  Shield,
  Trash2
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import backgroundVerificationService from '../../../api/services/backgroundVerificationService';
import toast from 'react-hot-toast';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../utils/stringUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import WarningPopup from '../../../components/WarningPopup';

const BackgroundVerification = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, canCreate, canUpdate } = usePermissions();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterVendor, setFilterVendor] = useState(searchParams.get('vendor') || 'all');
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteVerificationId, setDeleteVerificationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [pagination, setPagination] = useState({
    current: Number(searchParams.get('page')) || 1,
    pages: 1,
    total: 0,
    limit: Number(searchParams.get('limit')) || 10
  });
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Reset to page 1 when filters/search/date change
  useEffect(() => {
    setPagination((p) => ({ ...p, current: 1 }));
  }, [filterStatus, filterVendor, searchTerm, dateRange.startDate, dateRange.endDate]);

  // Load background verifications from API
  useEffect(() => {
    loadBackgroundVerifications();
    setSelectedRows(new Set());
  }, [filterStatus, filterVendor, searchTerm, pagination.current, pagination.limit, dateRange.startDate, dateRange.endDate]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'vendor', 'startDate', 'endDate', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterVendor && filterVendor !== 'all') params.set('vendor', filterVendor);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      if (pagination.current && pagination.current !== 1) params.set('page', String(pagination.current));
      if (pagination.limit && pagination.limit !== 10) params.set('limit', String(pagination.limit));

      return params;
    }, { replace: true });
  }, [searchTerm, filterStatus, filterVendor, dateRange, pagination.current, pagination.limit, setSearchParams]);

  const loadBackgroundVerifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pagination.current,
        limit: pagination.limit,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        vendor: filterVendor !== 'all' ? filterVendor : undefined,
        search: searchTerm || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined
      };

      const result = await backgroundVerificationService.getBackgroundVerifications(params);

      if (result && result.responseCode === 200 && result.data) {
        setVerifications(result.data.verifications || []);
        if (result.data.pagination) {
          setPagination((prev) => ({
            current: result.data.pagination.current || result.data.pagination.page || prev.current,
            pages: result.data.pagination.pages || result.data.pagination.totalPages || prev.pages,
            total: result.data.pagination.total || result.data.pagination.totalItems || prev.total,
            limit: result.data.pagination.limit || result.data.pagination.itemsPerPage || prev.limit,
          }));
        }
      } else {
        setError('Failed to load background verifications');
        toast.error('Failed to load background verifications');
      }
    } catch (error) {
      setError('Error loading background verifications');
      toast.error('Error loading background verifications');
    } finally {
      setLoading(false);
    }
  };

  // Permission-based access control functions
  const canExportData = () => {
    return hasPermission('recruitment', 'read') || hasPermission('onboarding', 'read');
  };

  const canInitiateVerification = () => {
    return canCreate('recruitment') || canCreate('onboarding');
  };

  const canViewVerification = () => {
    return hasPermission('recruitment', 'read') || hasPermission('onboarding', 'read') || hasPermission('self_service', 'read');
  };

  const canEditVerification = () => {
    // Permission-based edit access
    return canUpdate('recruitment') || canUpdate('onboarding');
  };

  const canSendReminder = () => {
    return hasPermission('recruitment', 'update') || hasPermission('onboarding', 'update');
  };

  const canRefreshStatus = () => {
    return hasPermission('recruitment', 'update') || hasPermission('onboarding', 'update');
  };

  const canDeleteVerification = () => {
    return hasPermission('recruitment', 'delete') || hasPermission('onboarding', 'delete');
  };

  const baseExportFilters = useMemo(() => {
    const f = {};
    const trimmed = searchTerm?.trim();
    if (trimmed) f.search = trimmed;
    if (filterStatus !== 'all') f.status = filterStatus;
    if (filterVendor !== 'all') f.vendor = filterVendor;
    if (dateRange.startDate) f.startDate = dateRange.startDate;
    if (dateRange.endDate) f.endDate = dateRange.endDate;
    return f;
  }, [
    searchTerm,
    filterStatus,
    filterVendor,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'background-verification',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? pagination.current,
            limit: overrideLimit ?? pagination.limit,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const fallbackName = `background-verification-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, pagination.current, pagination.limit]
  );

  const handleBgvSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error('No selected verifications to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} verification(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleBgvScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !verifications.length) {
          toast.error('No verifications to export on the current page.');
          return;
        }

        if (scope === 'filtered' && pagination.total === 0) {
          toast.error('No verifications match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [verifications.length, pagination.total, baseExportFilters, requestBackendExport]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleInitiateVerification = () => {
    navigate('/initiate_background_verification');
  };

  const handleViewVerification = (verificationId) => {
    navigate(`/view_background_verification/${verificationId}`);
  };

  const handleEditVerification = (verificationId) => {
    navigate(`/edit_background_verification/${verificationId}`);
  };



  const handleSendReminder = async (verificationId) => {
    try {
      const result = await backgroundVerificationService.sendReminder(verificationId);

      if (result && result.responseCode === 200) {
        toast.success('Reminder sent successfully');

        // Reload verification data to get updated history
        await loadBackgroundVerifications();
      } else {
        toast.error(result?.responseMessage || 'Failed to send reminder');
      }
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleRefreshStatus = async (verificationId) => {
    try {
      const result = await backgroundVerificationService.refreshStatus(verificationId);

      if (result && result.responseCode === 200) {
        toast.success('Status refreshed successfully');

        // Reload verification data to get updated status
        await loadBackgroundVerifications();
      } else {
        toast.error(result?.responseMessage || 'Failed to refresh status');
      }
    } catch (error) {
      toast.error('Failed to refresh status');
    }
  };

  const handleDeleteVerification = (verificationId) => {
    setDeleteVerificationId(verificationId);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDeleteVerification = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteVerification = async () => {
    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        const idsToDelete = Array.from(selectedRows);
        let successCount = 0;
        let errorCount = 0;

        for (const id of idsToDelete) {
          try {
            const result = await backgroundVerificationService.deleteBackgroundVerification(id);
            const isSuccess = (result && result.responseCode === 200) ||
              (result && result.responseCode === 201) ||
              (result && result.success === true) ||
              (result && result.status === 'success');

            if (isSuccess) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {
            errorCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} verifications deleted successfully`);
          setSelectedRows(new Set());
          loadBackgroundVerifications();
        }
        if (errorCount > 0) {
          toast.error(`Failed to delete ${errorCount} verifications`);
        }
      } else {
        if (!deleteVerificationId) return;
        const result = await backgroundVerificationService.deleteBackgroundVerification(deleteVerificationId);
        const isSuccess = (result && result.responseCode === 200) ||
          (result && result.responseCode === 201) ||
          (result && result.success === true) ||
          (result && result.status === 'success');

        if (isSuccess) {
          toast.success('Background verification deleted successfully');
          loadBackgroundVerifications();
        } else {
          toast.error(result?.responseMessage || 'Failed to delete background verification');
        }
      }
      setShowDeleteModal(false);
      setDeleteVerificationId(null);
      setIsBulkDelete(false);
    } catch (error) {
      toast.error('An error occurred while deleting');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteVerification = () => {
    setShowDeleteModal(false);
    setDeleteVerificationId(null);
    setIsBulkDelete(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'initiated': return 'bg-blue-50 text-blue-600';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed':
      case 'verified': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
      case 'initiated': return <Clock className="h-4 w-4" />;
      case 'in_progress': return <RefreshCw className="h-4 w-4" />;
      case 'completed':
      case 'verified': return <CheckCircle className="h-4 w-4" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const filteredVerifications = verifications;
  const showingFrom = (pagination.current - 1) * pagination.limit + 1;
  const showingTo = Math.min(pagination.current * pagination.limit, pagination.total);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Background Verification</h1>
          <p className="text-gray-600">Manage employee background verification process</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && canDeleteVerification() && (
            <PermissionGate permission="onboarding" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDeleteVerification}
                className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          {canExportData() && (
            <div className="relative shrink-0" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={<Download className={`h-4 w-4 ${exportLoading ? 'animate-spin' : ''}`} />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className={selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-blue-600' : ''}
              >
                {exportLoading ? 'Exporting...' : (selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export')}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleBgvSelectedFormatExport}
              />
            </div>
          )}
          {canInitiateVerification() && (
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleInitiateVerification}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Initiate Verification
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search candidate name, Email ID or ID..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Status</option>
              {/* <option value="pending">Pending</option> */}
              <option value="initiated">Initiated</option>
              <option value="in_progress">In Progress</option>
              <option value="verified">Verified</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(range) => {
                setDateRange(range);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
            />
          </div>

          {/* Clear Filters */}
          {(searchTerm || filterStatus !== "all" || dateRange.startDate || dateRange.endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("all");
                  setDateRange({ startDate: '', endDate: '' });
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Verification List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading verifications...</p>
              </div>
            </div>
          ) : filteredVerifications.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No verifications found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm || filterStatus !== 'all' || dateRange.startDate || dateRange.endDate
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No background verifications have been initiated yet.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Table View */}
              <div className="overflow-x-auto border border-gray-100 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={verifications.length > 0 && verifications.every(v => selectedRows.has(v.id))}
                          onChange={() => {
                            const currentPageIds = verifications.map(v => v.id);
                            const isAllSelected = currentPageIds.every(id => selectedRows.has(id));

                            const newSelected = new Set(selectedRows);
                            if (isAllSelected) {
                              currentPageIds.forEach(id => newSelected.delete(id));
                            } else {
                              currentPageIds.forEach(id => newSelected.add(id));
                            }
                            setSelectedRows(newSelected);
                          }}
                          className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                        />
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Candidate</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Position & ID</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Dates</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created At</th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredVerifications.map((verification) => (
                      <tr key={verification.id} className={`hover:bg-gray-50 transition-colors ${selectedRows.has(verification.id) ? 'bg-green-50' : ''}`}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(verification.id)}
                            onChange={() => {
                              const newSelectedRows = new Set(selectedRows);
                              if (newSelectedRows.has(verification.id)) {
                                newSelectedRows.delete(verification.id);
                              } else {
                                newSelectedRows.add(verification.id);
                              }
                              setSelectedRows(newSelectedRows);
                            }}
                            className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green h-4 w-4"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-gray-500" />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-gray-900 capitalize">{verification.candidateName}</div>
                              <div className="text-xs text-gray-500">{verification.email || verification.candidateEmail || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 capitalize">{verification.position}</div>
                          <div className="text-xs text-gray-500">ID: {verification.employeeId}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 capitalize">{verification.vendor?.name || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(verification.status)}`}>
                            {getStatusIcon(verification.status)}
                            <span className="ml-1 capitalize">{verification.status.replace('_', ' ')}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs">
                            <span className="text-gray-500">Init: </span>
                            <span className="text-gray-900 font-medium">{verification.initiatedDate ? formatDate(verification.initiatedDate, { includeTime: false }) : 'N/A'}</span>
                          </div>
                          <div className="text-xs mt-1">
                            <span className="text-gray-500">Exp: </span>
                            <span className="text-gray-900 font-medium">{verification.expectedCompletionDate ? formatDate(verification.expectedCompletionDate, { includeTime: false }) : 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-700 font-medium">
                            {verification.createdAt ? formatDate(verification.createdAt, { includeTime: true }) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {canViewVerification() && (
                              <button
                                onClick={() => handleViewVerification(verification.id)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            )}
                            {canEditVerification() && verification.status !== 'verified' && (
                              <button
                                onClick={() => handleEditVerification(verification.id)}
                                className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {canSendReminder() && verification.status === 'pending' && (
                              <button
                                onClick={() => handleSendReminder(verification.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                title="Send Reminder"
                              >
                                <Send className="h-4 w-4" />
                              </button>
                            )}
                            {canDeleteVerification() && (
                              <PermissionGate permission="onboarding" action="delete">
                                <button
                                  onClick={() => handleDeleteVerification(verification.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pagination Footer */}
      {pagination.total > pagination.limit && (
        <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-lg">
          <PaginationControls
            currentPage={pagination.current}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            limit={pagination.limit}
            hasNextPage={pagination.current < pagination.pages}
            hasPrevPage={pagination.current > 1}
            onPageChange={(p) => setPagination((prev) => ({ ...prev, current: p }))}
            onLimitChange={(l) => setPagination((prev) => ({ ...prev, limit: Number(l), current: 1 }))}
          />
        </div>
      )}



      {/* WarningPopup for Deletion */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={cancelDeleteVerification}
        onConfirm={confirmDeleteVerification}
        title={isBulkDelete ? "Delete Verifications" : "Delete Verification"}
        message={isBulkDelete
          ? `Are you sure you want to delete ${selectedRows.size} selected background verifications? This action cannot be undone.`
          : `Are you sure you want to delete this background verification? This action cannot be undone.`
        }
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Background Verification Data"
        showDateRange
        scopedFormatExport={{
          onExport: handleBgvScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div >
  );
};

export default BackgroundVerification;